import { useState, useEffect, useRef } from "react"
import { Button, Checkbox } from "react-aria-components"
import { httpGet, httpPost } from "../../../modules/Backend"
import { HttpStatusCode } from "axios"
import { EntityNames } from "../../../enums/EntityNames"
import LoadingButton from "../LoadingButton"
import CheckIcon from "../../../assets/icons/icon-check-white.svg"
import IconChevronLeft from "../../../assets/icons/icon-chevron-left.svg"
import IconBinRed from "../../../assets/icons/icon-bin-red.svg"
import IconPlusBlue from "../../../assets/icons/icon-plus-blue.svg"
import AdobeTextField from "../../AdobeAriaLib/AdobeTextField"
import AdobeNumberField from "../../AdobeAriaLib/AdobeNumberField"
import { modalSignal } from "./ModalContainer"
import { globalCanSubmitSignal, globalSubmittingSignal } from "../layout/Layout"
import { toast } from "react-toastify"

interface IReturnPartCasesPanel {
    caseId: string
    packingBoxId: string
    onReturn: () => void
    onCancel: () => void
    clearValues?: Function
}

interface ISpecimenData {
    id?: string
    specimenCode: string
    quantity: number | null
    originalQuantity?: number | null
    breadcrumb: string
    originalBreadcrumb?: string
    locationId?: string | null
    isNew?: boolean
    selectedForReturn?: boolean
}

export default function ReturnPartCasesPanel({
    caseId,
    packingBoxId,
    onReturn,
    onCancel,
    clearValues
}: IReturnPartCasesPanel) {
    const [specimens, setSpecimens] = useState<ISpecimenData[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [returningSpecimens, setReturningSpecimens] = useState<boolean>(false)
    const [packingBoxBreadcrumb, setPackingBoxBreadcrumb] = useState<string>('')
    const [packingBoxLocationId, setPackingBoxLocationId] = useState<string | null>(null)
    const [newSpecimenIndex, setNewSpecimenIndex] = useState<number | null>(null)
    const inputRefs = useRef<Map<number, HTMLInputElement>>(new Map())

    useEffect(() => {
        loadSpecimens()
    }, [caseId, packingBoxId])

    useEffect(() => {
        if (newSpecimenIndex !== null) {
            // Use requestAnimationFrame to ensure DOM is fully updated
            requestAnimationFrame(() => {
                const input = inputRefs.current.get(newSpecimenIndex)
                if (input) {
                    input.focus()
                }
                setNewSpecimenIndex(null)
            })
        }
    }, [newSpecimenIndex, specimens])

    async function loadSpecimens() {
        setLoading(true)
        try {
            const response = await httpGet(`api/${EntityNames.specimens}/listForReturn?CaseId=${caseId}&PackingBoxId=${packingBoxId}`)
            if (response.status === HttpStatusCode.Ok && response.data) {
                const packingBoxBreadcrumbValue = response.data.packingBoxBreadcrumb || ''
                setPackingBoxBreadcrumb(packingBoxBreadcrumbValue)
                setPackingBoxLocationId(response.data.packingBoxLocationId || null)

                const specimensList: ISpecimenData[] = response.data.specimens?.map((item: any) => ({
                    id: item.id,
                    specimenCode: item.specimenCode || '',
                    quantity: item.quantity,
                    originalQuantity: item.quantity, // Store original quantity
                    breadcrumb: item.breadcrumb || '',
                    originalBreadcrumb: item.breadcrumb || '', // Store original breadcrumb
                    locationId: item.locationId,
                    selectedForReturn: false
                })) || []
                setSpecimens(specimensList)
            }
        } catch (error) {
            console.error('Failed to fetch specimens:', error)
        } finally {
            setLoading(false)
        }
    }

    function handleSpecimenChange(index: number, field: 'specimenCode' | 'quantity', value: string | number | null) {
        const updatedSpecimens = [...specimens]
        if (field === 'quantity') {
            updatedSpecimens[index] = {
                ...updatedSpecimens[index],
                quantity: value === '' || value === null ? null : (typeof value === 'string' ? parseInt(value) || null : value)
            }
        } else {
            updatedSpecimens[index] = {
                ...updatedSpecimens[index],
                specimenCode: value === null ? '' : String(value)
            }
        }
        setSpecimens(updatedSpecimens)
    }

    function handleSpecimenCheckbox(specimenId: string, checked: boolean) {
        const updatedSpecimens = specimens.map(specimen => {
            if (specimen.id === specimenId) {
                // When checked, show packing box breadcrumb; when unchecked, show original breadcrumb
                return {
                    ...specimen,
                    selectedForReturn: checked,
                    breadcrumb: checked ? packingBoxBreadcrumb : (specimen.originalBreadcrumb || specimen.breadcrumb)
                }
            }
            return specimen
        })
        setSpecimens(updatedSpecimens)
    }

    function handleAddNewSpecimen() {
        const newBreadcrumb = packingBoxBreadcrumb || ''
        const newIndex = specimens.length
        setSpecimens([...specimens, {
            specimenCode: '',
            quantity: null,
            breadcrumb: newBreadcrumb,
            originalBreadcrumb: newBreadcrumb,
            isNew: true
        }])
        setNewSpecimenIndex(newIndex)
    }

    function handleRemoveSpecimen(index: number) {
        const updatedSpecimens = specimens.filter((_, i) => i !== index)
        setSpecimens(updatedSpecimens)
    }

    function handleQuantityKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
        const specimen = specimens[index]
        // Only add a new row if this is the last new specimen (bottom of list and isNew) and Tab is pressed
        const isLastNewSpecimen = specimen.isNew && index === specimens.length - 1
        if (isLastNewSpecimen && e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            handleAddNewSpecimen()
        }
    }

    function hasQuantityChanges(): boolean {
        // Check if any non-new specimen has a quantity change
        return specimens.some(s => {
            if (s.isNew || !s.id) return false
            return s.originalQuantity !== undefined && s.originalQuantity !== null && s.quantity !== s.originalQuantity
        })
    }

    function isValid(): boolean {
        // Check all new specimens have non-empty codes
        const newSpecimens = specimens.filter(s => s.isNew)
        if (newSpecimens.some(s => !s.specimenCode || s.specimenCode.trim() === '')) {
            return false
        }

        // Get all specimen codes (normalized to lowercase for comparison)
        const allSpecimenCodes = specimens.map(s => s.specimenCode?.trim().toLowerCase()).filter(code => code !== '')

        // Check for duplicate codes (case-insensitive)
        const uniqueCodes = new Set(allSpecimenCodes)
        if (uniqueCodes.size !== allSpecimenCodes.length) {
            return false
        }

        // Check only new specimens have quantities greater than 0
        // Existing specimens can have quantity 0 or null (not found state)
        if (newSpecimens.some(s => s.quantity === null || s.quantity === undefined || s.quantity <= 0)) {
            return false
        }

        // Check specimens to return must have quantity greater than 0
        const specimensToReturn = specimens.filter(s => !s.isNew && s.selectedForReturn && s.id)
        if (specimensToReturn.some(s => s.quantity === null || s.quantity === undefined || s.quantity <= 0)) {
            return false
        }

        return true
    }

    async function handleReturn() {
        setReturningSpecimens(true)
        try {
            const specimensToReturn = specimens
                .filter(s => !s.isNew && s.selectedForReturn && s.id)
                .map(s => ({ id: s.id!, specimenCode: s.specimenCode, quantity: s.quantity }))

            const newSpecimens = specimens
                .filter(s => s.isNew && s.specimenCode && s.specimenCode.trim() !== '')
                .map(s => ({ specimenCode: s.specimenCode, quantity: s.quantity }))

            // Updated specimens are those not marked for return (they may have quantity changes)
            const updatedSpecimens = specimens
                .filter(s => !s.isNew && !s.selectedForReturn && s.id)
                .map(s => ({ id: s.id!, specimenCode: s.specimenCode, quantity: s.quantity }))

            const payload = {
                caseId: caseId,
                packingBoxId: packingBoxId,
                specimensToReturn: specimensToReturn,
                newSpecimens: newSpecimens,
                updatedSpecimens: updatedSpecimens
            }

            const response = await httpPost(`api/${EntityNames.specimens}/returnExisting`, payload)
            if (response.status === HttpStatusCode.Ok) {
                // Mark all previously new specimens as no longer new so they can't be deleted
                setSpecimens(prevSpecimens =>
                    prevSpecimens.map(specimen => ({
                        ...specimen,
                        isNew: false
                    }))
                )
                if (clearValues) {
                    clearValues()
                }
            }
        } catch (error: any) {
            console.error('Failed to return specimens:', error)
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.response?.statusText || 
                                error?.message || 
                                'Failed to return specimens. Please try again.'
            toast.error(errorMessage, { position: "top-center" })
        } finally {
            setReturningSpecimens(false);
            globalCanSubmitSignal.value = { allowed: true };
            globalSubmittingSignal.value = { isBusy: false };
        }
    }

    return (
        <div className="return-part-cases display--flex flex__direction--column alignItems--center gap--xxs">
            <div className="return-part-cases__back-button-wrapper display--flex justifyContent--flex-start">
                <Button
                    className="button button--text-only button--with-icon"
                    onPress={onCancel}
                    isDisabled={returningSpecimens}>
                    <div><img src={IconChevronLeft} alt="" /></div>
                    <p className="side-panel__button-text">Back to cases</p>
                </Button>
            </div>

            <h2 className="margin__bottom--xxs">Return Part Cases</h2>

            <div className="return-part-cases__list-wrapper">
                {loading && (
                    <div className="return-part-cases__loading-overlay">
                        <div className="loaders loaders__full"></div>
                    </div>
                )}
                <div className="return-part-cases__list">
                    {specimens.map((specimen, index) => (
                        <div
                            key={specimen.id || `new-${index}`}
                            className="return-part-cases__row"
                        >
                            <div className="return-part-cases__spacer"></div>

                            <div className="return-part-cases__specimen-code">
                                {specimen.isNew ? (
                                    <AdobeTextField
                                        value={specimen.specimenCode || ''}
                                        label="Number"
                                        visibleLabel={true}
                                        icon={undefined}
                                        clickableIcon={false}
                                        onChangeText={(value: string) => handleSpecimenChange(index, 'specimenCode', value)}
                                        onBlur={undefined}
                                        required={false}
                                        className=""
                                        disabled={returningSpecimens}
                                        EnterKeyPress={() => { }}
                                        inputRef={(el: HTMLInputElement | null) => {
                                            if (el) {
                                                inputRefs.current.set(index, el)
                                                // If this is the newly added specimen, focus it immediately
                                                if (newSpecimenIndex === index) {
                                                    requestAnimationFrame(() => {
                                                        el.focus()
                                                    })
                                                }
                                            } else {
                                                inputRefs.current.delete(index)
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="return-part-cases__specimen-code-readonly">
                                        <label className="small margin__bottom--xxxs">Number</label>
                                        <p className="return-part-cases__specimen-code-text">{specimen.specimenCode || ''}</p>
                                    </div>
                                )}
                            </div>

                            <div className="return-part-cases__quantity">
                                <AdobeNumberField
                                    value={specimen.quantity?.toString() || ''}
                                    label="Quantity"
                                    visibleLabel={true}
                                    icon={undefined}
                                    onChangeText={(value: string) => handleSpecimenChange(index, 'quantity', value)}
                                    required={false}
                                    className=""
                                    disabled={returningSpecimens}
                                    onKeyDown={(e) => handleQuantityKeyDown(index, e)}
                                />
                            </div>

                            <div className="return-part-cases__breadcrumb">
                                <label className="small margin__bottom--xxxs">Current Location</label>
                                <p className="breadcrumb small return-part-cases__breadcrumb-text">
                                    {specimen.isNew
                                        ? (packingBoxBreadcrumb || '')
                                        : (specimen.selectedForReturn ? packingBoxBreadcrumb : (specimen.originalBreadcrumb || specimen.breadcrumb))
                                    }
                                </p>
                            </div>

                            <div className="return-part-cases__action">
                                {!specimen.isNew ? (
                                    // Only show checkbox if specimen location is different from packing box location
                                    specimen.locationId && specimen.locationId !== packingBoxLocationId ? (
                                        <div className="display--flex flex__direction--column">
                                            <label className="small margin__bottom--xxxs">Return?</label>
                                            <div className="display--flex alignItems--center">
                                                <Checkbox
                                                    isSelected={specimen.selectedForReturn || false}
                                                    onChange={(checked) => handleSpecimenCheckbox(specimen.id!, checked)}
                                                    isDisabled={returningSpecimens}>
                                                    <div className={`input-checkbox ${specimen.selectedForReturn ? "input-checkbox--selected" : "input-checkbox--unselected"}`}>
                                                        <img alt="" src={CheckIcon} />
                                                    </div>
                                                </Checkbox>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="display--flex flex__direction--column">
                                            <label className="small margin__bottom--xxxs" style={{ visibility: 'hidden' }}>Return?</label>
                                            <div className="display--flex alignItems--center">
                                                {/* Empty space to maintain alignment */}
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <div className="display--flex flex__direction--column">
                                        <label className="small margin__bottom--xxxs" style={{ visibility: 'hidden' }}>Return?</label>
                                        <div className="display--flex alignItems--center">
                                            <Button
                                                className="button button--text-only"
                                                onPress={() => handleRemoveSpecimen(index)}
                                                isDisabled={returningSpecimens}>
                                                <img src={IconBinRed} alt="Remove" className="return-part-cases__remove-button-icon" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    <hr className="return-part-cases__add-divider" />
                    <div className="return-part-cases__add-button-wrapper">
                        <Button
                            className="button button--text-only"
                            onPress={handleAddNewSpecimen}
                            isDisabled={returningSpecimens}>
                            <img src={IconPlusBlue} alt="Add New Specimen" className="return-part-cases__add-button-icon" />
                        </Button>
                    </div>
                </div>
            </div>

            {hasQuantityChanges() && (
                <div className="width--full margin__bottom--xxxs">
                    <div className="box box--warning width--full">
                        <p>Please double check quantity changes before saving.</p>
                    </div>
                </div>
            )}

            <div className="return-part-cases__actions-wrapper">
                <div className="return-part-cases__actions">
                    {!isValid() && (
                        <p className="error">
                            {(() => {
                                const newSpecimens = specimens.filter(s => s.isNew)
                                const specimensToReturn = specimens.filter(s => !s.isNew && s.selectedForReturn && s.id)
                                const hasInvalidNewSpecimens = newSpecimens.some(s => !s.specimenCode || s.specimenCode.trim() === '' || s.quantity === null || s.quantity === undefined || s.quantity <= 0)
                                const hasInvalidReturnSpecimens = specimensToReturn.some(s => s.quantity === null || s.quantity === undefined || s.quantity <= 0)
                                const allSpecimenCodes = specimens.map(s => s.specimenCode?.trim().toLowerCase()).filter(code => code !== '')
                                const uniqueCodes = new Set(allSpecimenCodes)
                                const hasDuplicates = uniqueCodes.size !== allSpecimenCodes.length

                                if (hasInvalidReturnSpecimens) {
                                    return 'All specimens to return must have a quantity greater than 0.'
                                }
                                if (hasInvalidNewSpecimens) {
                                    return 'All new specimens must have a number and quantity greater than 0.'
                                }
                                if (hasDuplicates) {
                                    return 'All numbers must be unique.'
                                }
                                return 'All numbers must be unique and all new quantities must be provided.'
                            })()}
                        </p>
                    )}
                    <LoadingButton
                        className="button button--small button--primary"
                        onPress={handleReturn}
                        buttonText="Save"
                        loadingText="Saving"
                        isDisabled={returningSpecimens || loading || !isValid()}
                        isLoading={returningSpecimens}>
                        <></>
                    </LoadingButton>
                </div>
            </div>
        </div >
    )
}
